// Centro de ayuda: qué hace cada cosa y, sobre todo, cómo conviene usarla.
// No es un manual de software; es la diferencia entre una libreta digital y
// algo que de verdad evita que se pierdan cosas.

import { iconoChevron } from "../iconos";

const seccion = (titulo: string, cuerpo: string) => `
  <details class="fila">
    <summary class="flex items-center justify-between gap-2 p-4 font-medium">
      <span>${titulo}</span>
      <span class="shrink-0 text-apagado">${iconoChevron}</span>
    </summary>
    <div class="space-y-3 border-t border-borde p-4 text-sm leading-relaxed text-apagado">
      ${cuerpo}
    </div>
  </details>`;

const consejo = (texto: string) =>
  `<p class="rounded-lg border-l-2 border-acento bg-realce px-3 py-2 text-texto">${texto}</p>`;

/** Pantalla de ejemplo, dibujada. No es una captura: no queda vieja. */
const ejemplo = (titulo: string, cuerpo: string) => `
  <div class="mt-3">
    <p class="mb-1 text-[11px] uppercase tracking-wider text-apagado">${titulo}</p>
    <div class="mock">${cuerpo}</div>
  </div>`;

const filaMock = (texto: string, chips: string[], hecha = false) => `
  <div class="mock-fila">
    <span class="mock-tilde">${hecha ? "✓" : ""}</span>
    <div class="min-w-0 flex-1">
      <div class="${hecha ? "text-apagado line-through" : "text-texto"}">${texto}</div>
      <div class="mt-1 flex flex-wrap gap-1">${chips.map((c) => `<span class="mock-chip">${c}</span>`).join("")}</div>
    </div>
  </div>`;

export function renderAyuda(contenedor: HTMLElement): void {
  contenedor.innerHTML = `
    <h1 class="titulo-pagina">Cómo se usa</h1>
    <p class="subtitulo">Qué hace cada pantalla y cómo sacarle el jugo.</p>

    <div class="tarjeta mt-4 text-sm leading-relaxed">
      <p class="text-texto">
        Míster Memoria no está para organizar tu trabajo: está para que <b>no se te pierda nada</b>
        y para que en diez segundos sepas qué hacer ahora. Todo lo demás es decoración alrededor
        de eso.
      </p>
    </div>

    <h2 class="mt-6 text-sm font-semibold uppercase tracking-wider text-acento">
      Las seis costumbres que hacen que funcione
    </h2>
    <div class="mt-2 space-y-2">
      ${seccion(
        "1 · Cargala en el momento, aunque sea con dos palabras",
        `<p>El alta rápida de la ficha del proyecto es de <b>dos toques</b> justamente para esto:
          escribís el título, Enter, y sigue tu vida. Después la completás.</p>
         ${ejemplo(
           "Abajo de las tareas del proyecto",
           `<div class="flex gap-2">
              <span class="mock-campo flex-1">arreglar el iva de la factura</span>
              <span class="mock-btn">+</span>
            </div>
            <p class="mt-2 text-[10px] text-apagado">Enter, y ya está cargada como pendiente, prioridad 3.</p>`,
         )}
         ${consejo(
           "Una tarea mal escrita cargada hoy vale mil veces más que una tarea perfecta que ibas a cargar mañana y nunca cargaste.",
         )}`,
      )}
      ${seccion(
        "2 · La prioridad 1 es para lo que arde, nada más",
        `<p>Si todo es P1, no hay P1. La lista se ordena por prioridad, así que en el momento en que
          marcás todo como urgente, la app deja de ayudarte y volvés a la libreta.</p>
         <p>La escala va de <b>1 a 10</b>, donde 1 es lo más importante. El valor por defecto es
          <b>P3</b>, y está bien que la mayoría se quede ahí o más abajo.</p>
         <p>Sólo se pintan de color <b>P1, P2 y P3</b>. De 4 para abajo van en gris a propósito:
          si media escala tuviera color, volveríamos a una pantalla donde todo grita.</p>`,
      )}
      ${seccion(
        "3 · Fecha sólo a lo que realmente vence",
        `<p>El vencimiento es lo que decide si algo aparece en <b>Hoy</b>. Una fecha inventada
          "para acordarme" ensucia la agenda y en dos semanas la mirás por arriba.</p>
         ${consejo(
           "Sin fecha la tarea no desaparece: sigue en su proyecto, ordenada por prioridad. La fecha es para lo que tiene consecuencia si se pasa.",
         )}`,
      )}
      ${seccion(
        "4 · Usá «esperando a» para los trámites",
        `<p>Es la función más importante de la app y la que ninguna otra hace. Si no podés programar
          hasta que el cliente saque el dominio, marcá la tarea de programar como que
          <b>espera</b> a la del trámite.</p>
         <p>La tarea bloqueada se apaga, sale de la agenda —no podés hacerla igual— y el trámite
          aparece en el bloque <b>Bloqueando a otras</b> con cuántas cosas está frenando.</p>
         ${ejemplo(
           "Así se ve en la ficha del proyecto",
           filaMock("Registrar el dominio a nombre del cliente", ["trámite", "p1", "vence 10/08"]) +
             filaMock("Configurar el DNS y publicar", [
               "modificación",
               "esperando: registrar el dominio",
             ]) +
             `<p class="mt-2 text-[10px] text-apagado">La segunda queda apagada hasta que la primera esté hecha.</p>`,
         )}
         ${consejo(
           "Cuando marcás hecho el trámite, la otra se destraba sola. No hay que acordarse de nada.",
         )}`,
      )}
      ${seccion(
        "5 · Empezá el día por «Hoy» y marcá desde ahí",
        `<p>Si el bloque de arriba está vacío, estás al día: no hace falta que revises proyecto por
          proyecto. Y podés marcar hecho <b>de un toque desde la agenda</b>, sin entrar a ningún
          lado.</p>
         ${ejemplo(
           "Un lunes cualquiera",
           `<p class="text-[10px] font-semibold uppercase tracking-wider" style="color:#CB6A5C">Vencido o vence hoy (1)</p>` +
             filaMock("Renovar el dominio de la panadería", ["venció 01/08", "anual"]) +
             `<p class="mt-3 text-[10px] font-semibold uppercase tracking-wider text-apagado">Esta semana (2)</p>` +
             filaMock("Arreglar el redondeo de la lista", ["bug", "p1", "vence 04/08"]) +
             filaMock("Pasarle el precio de la home", ["modificación", "p3", "vence 05/08"]) +
             `<p class="mt-2 text-[10px] text-apagado">Tocás el círculo de la izquierda y la tarea desaparece de la lista.</p>`,
         )}`,
      )}
      ${seccion(
        "6 · Lo que vuelve es un recordatorio, no una tarea",
        `<p>Renovar un dominio, pagar un plan, hacer el respaldo: eso va en <b>Recordar</b>. Al
          marcarlo cumplido la fecha se corre sola al próximo período y no lo tenés que volver a
          cargar nunca.</p>
         ${consejo(
           "Poné «avisar días antes» según lo que tardes en resolverlo: 30 días para un dominio, 5 para un pago. Antes de eso ni te aparece.",
         )}`,
      )}
    </div>

    <h2 class="mt-8 text-sm font-semibold uppercase tracking-wider text-acento">
      Pantalla por pantalla
    </h2>
    <div class="mt-2 space-y-2">
      ${seccion(
        "Hoy — ¿qué hago ahora?",
        `<p>Es la pantalla de entrada y sale entera del servidor, en cinco bloques:</p>
         <ul class="ml-4 list-disc space-y-1">
           <li><b>Vencido o vence hoy</b> — tareas y recordatorios. Arriba de todo, siempre.</li>
           <li><b>Esta semana</b> — los próximos siete días.</li>
           <li><b>Más adelante</b> — recordatorios que pidieron aviso con anticipación.</li>
           <li><b>Bloqueando a otras</b> — lo que está frenando trabajo, con cuántas tareas frena.</li>
           <li><b>Enviados sin respuesta</b> — presupuestos mandados, con cuántos días hace.</li>
           <li><b>Proyectos activos</b> — por prioridad, con lo pendiente de cada uno.</li>
         </ul>
         <p>Los proyectos <b>pausados</b> no aparecen acá aunque tengan tareas vivas: para eso están
           pausados. Y las tareas bloqueadas tampoco, porque no las podés hacer todavía.</p>`,
      )}
      ${seccion(
        "Proyectos",
        `<p>La lista va por prioridad, con el conteo de pendientes y bloqueadas de cada uno. Los
          pausados van al final, apagados.</p>
         <p>Adentro de cada proyecto están sus tareas ordenadas por prioridad y, a igual prioridad,
          por vencimiento más cercano. Las hechas se pliegan abajo: son historial, no ruido.</p>
         <p><b>Estados:</b> Idea (existe pero no arrancó) → En desarrollo → En producción →
          Archivado. Y Pausado, al que se entra y sale desde cualquiera.</p>
         ${consejo(
           "Archivar es una baja lógica: el proyecto y sus tareas quedan guardados para siempre, sólo dejan de molestar. Nada se borra nunca en esta app.",
         )}`,
      )}
      ${seccion(
        "Precios — presupuestos",
        `<p>Se crea un <b>borrador</b>, se le cargan renglones (descripción, cantidad, precio) y el
          total se recalcula mientras escribís. La cantidad puede llevar decimales, porque son horas:
          1,5 h × $2.500.</p>
         <p>Cuando lo mandás, tocá <b>Marcar enviado</b>: ahí el presupuesto <b>se congela</b>. Un
          presupuesto enviado o aceptado no se edita — los precios que ya vio el cliente no se tocan.
          Si cambió algo, se anula y se hace uno nuevo.</p>
         <p><b>Imprimir / PDF</b> arma la hoja con número, fecha, cliente, renglones, total, validez
          y las cláusulas al pie. Desde el celular, elegí "Guardar como PDF" en el diálogo de
          impresión.</p>
         ${consejo(
           "El número es correlativo y lo pone la app sola. Vencido no es un estado que se guarde: un enviado cuya validez ya pasó se muestra vencido solo.",
         )}`,
      )}
      ${seccion(
        "Clientes",
        `<p>Nombre, contacto, teléfono, email y notas. Nada más: no es un CRM, no hay pipeline ni
          seguimiento comercial.</p>
         <p>Al abrir uno ves sus proyectos y todos sus presupuestos con el estado de cada uno.</p>`,
      )}
      ${seccion(
        "Cláusulas — y las tareas que se crean solas",
        `<p>Es el catálogo de condiciones de trabajo, agrupado por categoría: pago, plazos, alcance,
          soporte, propiedad, hosting. Se escribe una vez y se adjunta a los presupuestos que quieras.</p>
         <p>Lo importante: cada cláusula puede tener <b>tareas asociadas</b>. Al aceptar un
          presupuesto, esas tareas <b>se crean solas</b> en el proyecto, con vencimiento contado
          desde el día de la aceptación.</p>
         <p>Ejemplo: la cláusula "hosting a cargo del cliente" genera al aceptarse "registrar el
          dominio a su nombre" (para el cliente, a 7 días), "pedir accesos de DNS" (tuya, a 10) y
          "contratar Cloudflare si hace falta" (tuya, a 14).</p>
         ${consejo(
           "Adjuntá las cláusulas ANTES de mandar el presupuesto. Si lo aceptás sin ellas, no se genera nada, y un presupuesto aceptado ya no se edita.",
         )}
         <p>Al adjuntar podés <b>retocar el texto</b> sólo para ese presupuesto, sin ensuciar el
          catálogo. Y si el presupuesto dispara tareas, necesita tener un proyecto asignado: si no,
          la app te frena y te avisa.</p>`,
      )}
      ${seccion(
        "Recordar — lo que vuelve",
        `<p>Título, próxima fecha, cada cuánto (una vez, mensual, trimestral, anual) y con cuántos
          días de anticipación querés que aparezca.</p>
         <p>Al marcarlo cumplido, si es recurrente la fecha salta al próximo período que todavía no
          pasó —aunque hayas estado meses sin abrir la app— y sigue vivo. Si era por única vez, se
          apaga.</p>`,
      )}
      ${seccion(
        "Grafo",
        `<p>Todo junto y conectado: proyectos (naranja), clientes (verde), tareas (gris) y
          presupuestos (blanco). Las <b>líneas rojas punteadas</b> son las tareas que frenan a otras.</p>
         <p>Tocá un nodo para ir a su ficha. Con los filtros de arriba podés apagar las tareas
          cuando hay demasiadas y querés ver sólo el mapa de proyectos y clientes.</p>
         <p>Desde la ficha de un proyecto podés <b>vincularlo con otro</b> a mano, para relaciones
          que no salen del modelo ("este tiene que ver con aquel").</p>`,
      )}
      ${seccion(
        "Bitácora — «¿en qué estaba?»",
        `<p>Dentro de cada proyecto hay un cuaderno de <b>notas fechadas</b>. No tiene estados ni
          vencimientos: se escribe y se lee. Es para cuando volvés a un proyecto después de tres
          semanas y no te acordás dónde habías quedado.</p>
         ${ejemplo(
           "Bitácora de un proyecto",
           `<div class="mock-fila"><div class="min-w-0 flex-1">
              <span class="font-mono text-[10px] text-apagado">02/08/2026</span>
              <p class="mt-1 text-texto">Trancado con el redondeo del IVA: el total no cierra por 1 centavo
                cuando hay descuento. Probé redondear por ítem y empeora. Seguir por redondear al final.</p>
              <div class="mt-1 flex gap-1"><span class="mock-chip">iva</span><span class="mock-chip">trancado</span></div>
            </div></div>`,
         )}
         ${consejo(
           "Escribila como se la contarías a vos dentro de un mes: qué probaste, qué no anduvo y por dónde seguir. Eso es lo que no está en el código.",
         )}
         <p>Las etiquetas son texto libre separado por coma, y con más de tres notas aparece un
          buscador arriba.</p>`,
      )}
      ${seccion(
        "Horas estimadas",
        `<p>Cada tarea puede llevar una <b>estimación</b> en horas. En la ficha del proyecto se suma
          lo pendiente: <i>"lo que queda suma ≈ 12 h estimadas"</i>.</p>
         <p><b>No es un cronómetro.</b> La app no mide cuánto te llevó de verdad, no hay botón de
          empezar ni de parar. Es un número que ponés vos, para dos cosas: saber si lo pendiente
          entra en la semana, y presupuestar con una base en vez de a ojo.</p>`,
      )}
      ${seccion(
        "Registro",
        `<p>Todo lo que cambió en la app, agrupado por día: qué fue, a qué hora y con qué usuario.
          Se escribe solo y <b>no se puede editar ni borrar</b> desde la app; si se pudiera tocar,
          no serviría de nada.</p>
         <p><a href="#/auditoria" class="text-acento hover:underline">Ver el registro</a></p>`,
      )}
    </div>

    <h2 class="mt-8 text-sm font-semibold uppercase tracking-wider text-acento">
      Lo que conviene saber
    </h2>
    <div class="mt-2 space-y-2">
      ${seccion(
        "Sin señal",
        `<p>La app se instala en el celular y abre sin conexión: te muestra <b>lo último que
          cargaste</b>, con un aviso arriba. Podés mirar, no cargar cosas nuevas.</p>
         <p>Lo que escribas sin señal no se guarda en una cola: esperá a tener conexión.</p>`,
      )}
      ${seccion(
        "Lo que la app no hace, a propósito",
        `<ul class="ml-4 list-disc space-y-1">
           <li>No cronometra horas: no es un time tracker.</li>
           <li>No emite facturas ni recibos. Presupuestos, nada más.</li>
           <li>No manda mails ni notificaciones: los recordatorios se <b>ven</b> al abrirla.</li>
           <li>No borra nada. Todo es baja lógica: archivar, anular, apagar.</li>
         </ul>`,
      )}
      ${seccion(
        "Respaldos",
        `<p>Cloudflare guarda 30 días de historia de la base, y <b>sólo 30</b>. Para un respaldo de
          verdad, desde la compu:</p>
         <pre class="overflow-x-auto rounded-lg bg-realce p-3 text-xs text-texto">npx wrangler d1 export DB --remote --output=respaldos/mm.sql</pre>
         ${consejo("Vale la pena hacerlo cada tanto, sobre todo antes de tocar la base a mano.")}`,
      )}
    </div>`;
}
